import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { X, MapPin, AlertCircle, CheckCircle2, Loader2 } from 'lucide-react';
import { clientsAPI } from '@shared/apiClient';
import toast from 'react-hot-toast';

export default function CityCleanupModal({ isOpen, onClose }) {
  const queryClient = useQueryClient();
  const [selectedGroups, setSelectedGroups] = useState(new Map()); // Map of group index -> selected city name

  // Fetch duplicate cities
  const { data, isLoading, refetch } = useQuery(
    'duplicateCities',
    () => clientsAPI.getDuplicateCities(),
    { enabled: isOpen }
  );

  // Standardize city mutation
  const standardizeMutation = useMutation(
    ({ fromCity, toCity }) => clientsAPI.standardizeCity({ fromCity, toCity }),
    {
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to standardize city');
      }
    }
  );

  const handleSelectCity = (groupIndex, cityName) => {
    setSelectedGroups(prev => {
      const newMap = new Map(prev);
      if (newMap.get(groupIndex) === cityName) {
        // Deselect if already selected
        newMap.delete(groupIndex);
      } else {
        // Select the city
        newMap.set(groupIndex, cityName);
      }
      return newMap;
    });
  };

  const handleStandardize = async (groupIndex, toCity) => {
    const allGroups = [...(data?.data?.exactDuplicates || []), ...(data?.data?.similarCities || [])];
    const group = allGroups[groupIndex];
    if (!group) return;

    const variationsToStandardize = group.variations.filter(v => v.city !== toCity);
    if (variationsToStandardize.length === 0) {
      toast.error('No cities to standardize');
      return;
    }

    try {
      const promises = variationsToStandardize.map(variation =>
        standardizeMutation.mutateAsync({ fromCity: variation.city, toCity })
      );
      await Promise.all(promises);
      toast.success(`Successfully standardized ${variationsToStandardize.length} city name(s) to "${toCity}"`);
      // Remove this group from selections
      setSelectedGroups(prev => {
        const newMap = new Map(prev);
        newMap.delete(groupIndex);
        return newMap;
      });
      // Refetch duplicate cities and invalidate clients query
      refetch();
      queryClient.invalidateQueries('clients');
    } catch (error) {
      toast.error('Some cities failed to standardize');
    }
  };

  const handleStandardizeAll = async () => {
    const promises = [];
    selectedGroups.forEach((toCity, groupIndex) => {
      const allGroups = [...(data?.data?.exactDuplicates || []), ...(data?.data?.similarCities || [])];
      const group = allGroups[groupIndex];
      if (group) {
        group.variations.forEach(variation => {
          if (variation.city !== toCity) {
            promises.push(
              standardizeMutation.mutateAsync({ fromCity: variation.city, toCity })
            );
          }
        });
      }
    });

    try {
      await Promise.all(promises);
      setSelectedGroups(new Map());
      toast.success('All selected cities have been standardized!');
      // Refetch duplicate cities and invalidate clients query
      refetch();
      queryClient.invalidateQueries('clients');
    } catch (error) {
      toast.error('Some cities failed to standardize');
    }
  };

  if (!isOpen) return null;

  const exactDuplicates = data?.data?.exactDuplicates || [];
  const similarCities = data?.data?.similarCities || [];
  const allGroups = [...exactDuplicates, ...similarCities];
  const hasSelections = selectedGroups.size > 0;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div 
          className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
          onClick={onClose}
        />

        {/* Modal panel */}
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
          {/* Header */}
          <div className="bg-gray-50 px-4 py-5 sm:px-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-medium text-gray-900">
                  Clean Up City Names
                </h3>
                <p className="mt-1 text-sm text-gray-500">
                  Find and merge duplicate or similar city names
                </p>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="text-gray-400 hover:text-gray-500"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
          </div>

          {/* Body */}
          <div className="px-4 py-5 sm:p-6 max-h-[60vh] overflow-y-auto">
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
                <span className="ml-3 text-gray-600">Loading duplicate cities...</span>
              </div>
            ) : allGroups.length === 0 ? (
              <div className="text-center py-12">
                <CheckCircle2 className="mx-auto h-12 w-12 text-green-500" />
                <h3 className="mt-4 text-lg font-medium text-gray-900">No Duplicates Found</h3>
                <p className="mt-2 text-sm text-gray-500">
                  All city names appear to be unique. Great job!
                </p>
              </div>
            ) : (
              <div className="space-y-6">
                {/* Summary */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-start">
                    <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5" />
                    <div className="ml-3">
                      <p className="text-sm font-medium text-blue-900">
                        Found {allGroups.length} group(s) of potential duplicate cities
                      </p>
                      <p className="mt-1 text-sm text-blue-700">
                        Total unique cities: {data?.data?.totalUniqueCities || 0}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Instructions */}
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <p className="text-sm text-gray-700">
                    <strong>How to use:</strong> Click on a city name to select it as the standard name. 
                    Then click "Standardize" to merge all other variations in that group to the selected name.
                    The most common city name is shown first in each group.
                  </p>
                </div>

                {/* City Groups */}
                {allGroups.map((group, groupIndex) => {
                  const selectedCity = selectedGroups.get(groupIndex);
                  const isExactDuplicate = !group.isSimilar;

                  return (
                    <div key={groupIndex} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center">
                          <MapPin className="h-5 w-5 text-gray-400 mr-2" />
                          <span className="text-sm font-medium text-gray-700">
                            {isExactDuplicate ? 'Exact Duplicates' : 'Similar Cities'}
                          </span>
                          <span className="ml-2 text-xs text-gray-500">
                            ({group.totalClients} customer{group.totalClients !== 1 ? 's' : ''})
                          </span>
                        </div>
                        {selectedCity && (
                          <button
                            onClick={() => handleStandardize(groupIndex, selectedCity)}
                            disabled={standardizeMutation.isLoading}
                            className="btn btn-primary btn-sm"
                          >
                            {standardizeMutation.isLoading ? 'Standardizing...' : 'Standardize Group'}
                          </button>
                        )}
                      </div>

                      <div className="space-y-2">
                        {group.variations.map((variation, idx) => {
                          const isSelected = selectedCity === variation.city;
                          return (
                            <div
                              key={idx}
                              onClick={() => handleSelectCity(groupIndex, variation.city)}
                              className={`flex items-center justify-between p-3 rounded-md cursor-pointer transition-colors ${
                                isSelected
                                  ? 'bg-primary-50 border-2 border-primary-500'
                                  : 'bg-gray-50 border-2 border-transparent hover:bg-gray-100'
                              }`}
                            >
                              <div className="flex items-center">
                                <div className={`w-4 h-4 rounded-full border-2 mr-3 flex items-center justify-center ${
                                  isSelected
                                    ? 'border-primary-500 bg-primary-500'
                                    : 'border-gray-300'
                                }`}>
                                  {isSelected && (
                                    <div className="w-2 h-2 rounded-full bg-white" />
                                  )}
                                </div>
                                <span className={`font-medium ${
                                  isSelected ? 'text-primary-900' : 'text-gray-900'
                                }`}>
                                  {variation.city}
                                </span>
                              </div>
                              <span className="text-sm text-gray-500">
                                {variation.count} customer{variation.count !== 1 ? 's' : ''}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse border-t border-gray-200">
            {hasSelections && (
              <button
                onClick={handleStandardizeAll}
                disabled={standardizeMutation.isLoading || allGroups.length === 0}
                className="w-full inline-flex justify-center items-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-green-600 text-base font-medium text-white hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {standardizeMutation.isLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Standardizing...
                  </>
                ) : (
                  `Standardize All Selected (${selectedGroups.size})`
                )}
              </button>
            )}
            <button
              type="button"
              onClick={onClose}
              className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}














